import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Shows the USD/UZS rate of a month: the rate its UZS incomes were entered at.
 * The backend computes that month's USD figures with it, so pages also use it
 * (via onRate) for their own USD -> UZS conversions instead of today's rate.
 *
 * @param {object}   props
 * @param {number}   props.month   - 1-12
 * @param {number}   props.year
 * @param {Function} [props.onRate] - Called with the rate (number) or null.
 */
export function MonthRate({ month, year, onRate }) {
  const { t } = useTranslation('shared');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // drop the previous month's rate until this month's arrives
    setData(null);
    onRate?.(null);
    axios
      .get(`${API_URL}/api/exchange-rates/month?month=${month}&year=${year}`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        onRate?.(res.data?.rate || null);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Error fetching month rate:', e);
        setData(null);
        onRate?.(null);
      });
    return () => { cancelled = true; };
    // onRate is a state setter in the callers; refetch only when the month changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  if (!data?.rate) return null;
  const entry = data.source === 'entry';
  return (
    <p
      className="text-xs text-slate-500 dark:text-slate-400"
      title={entry ? t('monthRate.entryHint') : t('monthRate.currentHint')}
      data-testid="month-rate"
    >
      {t(entry ? 'monthRate.label' : 'monthRate.currentLabel', {
        rate: Number(data.rate).toLocaleString(undefined, { maximumFractionDigits: 2 }),
      })}
    </p>
  );
}
